import React from "react";
import { Dropdown } from "react-bootstrap";
import useDropdown from "../../../../hooks/dropdown.hook";
import ChevronXSIcon from "../../../icons/chevron-xs.icon";
import CheckIcon from "../../../icons/check.icon";
import styles from "./selected-filter-dropdown.module.scss";

/**
 * General dropdown component for filter options
 * @param {Object} props
 * @param {Array} props.options - Array of option objects with { id, label, value, icon? }
 * @param {string|number|Array} props.selectedValue - Currently selected option value(s) - can be array for multiple selection
 * @param {Function} props.onSelect - Callback when option is selected (value) => void
 * @param {string} props.placeholder - Placeholder text when no option is selected
 * @param {string} props.className - Additional CSS class name
 * @param {string} props.variant - Style variant: "default" or "chip"
 * @param {string} props.displayText - Custom display text for the toggle (overrides selected option label)
 * @param {string} props.headerTitle - Header title to display above options (e.g., "Distributors")
 * @param {boolean} props.multiple - Enable multiple selection mode
 * @returns {JSX.Element}
 */
export const SelectedFilterDropdown = ({
  options = [],
  selectedValue = null,
  onSelect = () => {},
  placeholder = "Select option",
  className = "",
  variant = "default",
  displayText: customDisplayText = null,
  headerTitle = null,
  multiple = false,
}) => {
  const { isOpen, refBtn, refMenu, setIsOpen } = useDropdown({});

  const isSelected = (value) => {
    if (multiple) {
      const selectedArray = Array.isArray(selectedValue) ? selectedValue : [];
      return selectedArray.includes(value);
    }
    return selectedValue === value;
  };

  const selectedOption = multiple 
    ? null 
    : options.find((opt) => opt.value === selectedValue);
  
  const displayText = customDisplayText || (selectedOption ? selectedOption.label : placeholder);

  const handleSelect = (eventKey, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    const option = options.find((opt) => opt.value === eventKey || opt.id === eventKey);
    if (option) {
      if (multiple) {
        const currentSelected = Array.isArray(selectedValue) ? selectedValue : [];
        const newSelected = isSelected(option.value)
          ? currentSelected.filter((v) => v !== option.value)
          : [...currentSelected, option.value];
        console.log('Selected filter option:', option.label, 'New selection:', newSelected);
        if (onSelect) {
          onSelect(newSelected);
        }
      } else {
        console.log('Selected filter option:', option.label, 'Value:', option.value);
        if (onSelect) {
          onSelect(option.value);
        }
        setIsOpen(false);
      }
    }
  };

  const toggleClassName = variant === "chip" ? styles.toggleChip : styles.toggle;
  const toggleInnerClassName = variant === "chip" ? styles.toggleInnerChip : styles.toggleInner;
  const toggleTextClassName = variant === "chip" ? styles.toggleTextChip : styles.toggleText;
  const toggleIconClassName = variant === "chip" ? styles.toggleIconChip : styles.toggleIcon;

  return (
    <Dropdown
      show={isOpen}
      onToggle={() => setIsOpen((prev) => !prev)}
      className={`${styles.dropdown} ${className}`}
      onSelect={handleSelect}
    >
      <Dropdown.Toggle as="div" ref={refBtn} className={toggleClassName}>
        <div className={toggleInnerClassName}>
          <div className={styles.toggleTextWrapper}>
            <div className={toggleTextClassName}>{displayText}</div>
          </div>
          <div className={toggleIconClassName}>
            <ChevronXSIcon width={9} height={9} style={{ color: "var(--grayscale-gray-80)" }} />
          </div>
        </div>
      </Dropdown.Toggle>
      <Dropdown.Menu ref={refMenu} className={styles.menu}>
        {headerTitle && (
          <div className={styles.menuHeader}>
            <div className={styles.menuHeaderTitle}>{headerTitle}</div>
          </div>
        )}
        {options.map((option) => {
          const optionIsSelected = isSelected(option.value);
          return (
            <Dropdown.Item
              key={option.id || option.value}
              eventKey={option.value || option.id}
              className={styles.option}
              active={optionIsSelected}
              onClick={(e) => handleSelect(option.value || option.id, e)}
            >
              <div className={styles.optionContent}>
                {multiple && (
                  optionIsSelected ? (
                    <div className={styles.checkboxChecked}>
                      <CheckIcon className={styles.checkIcon} />
                    </div>
                  ) : (
                    <div className={styles.checkboxUnchecked} />
                  )
                )}
                {option.icon && (
                  <div className={styles.optionIcon}>
                    {option.icon}
                  </div>
                )}
                <span className={styles.optionText}>{option.label}</span>
              </div>
            </Dropdown.Item>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
};
